<template>
  <div v-if="category" class="category-container">
    <h3>{{ category.name }}</h3>
    
    <!-- Subcategories -->
    <p v-if="category.subcategories && category.subcategories.length">Subkategori:</p>
    <ul v-if="category.subcategories && category.subcategories.length">
      <li v-for="sub in category.subcategories" :key="sub.id">
        <router-link :to="`/categories/${sub.id}`">{{ sub.name }}</router-link>
      </li>
    </ul>

    <!-- Book List -->
    <h4>Buku dalam Kategori {{ category.name }}</h4>
    <BookList :category="category.id" />
  </div>

  <div v-else class="loading">
    <p>Loading...</p>
  </div>
</template>

<script>
import axios from "axios";
import BookList from "@/components/BookList.vue";
import { useHead } from '@unhead/vue';
import { computed, ref } from 'vue';

export default {
  name: "CategoryPage",
  components: { BookList },
  
  setup() {
    const category = ref(null);
    
    // Computed properties for meta tags
    const pageTitle = computed(() => {
      return category.value ? `${category.value.name} - Kategori Buku | HobiBaca` : 'Kategori Buku | HobiBaca';
    });
    
    const pageDescription = computed(() => {
      if (!category.value) return 'Jelajahi berbagai kategori buku di HobiBaca';
      const subcategories = category.value.subcategories || [];
      
      if (subcategories.length) {
        const subNames = subcategories.map(sub => sub.name).join(', ');
        return `Temukan buku-buku dalam kategori ${category.value.name}. Subkategori: ${subNames}. Koleksi lengkap HobiBaca.`;
      }
      
      return `Temukan buku-buku dalam kategori ${category.value.name}. Koleksi lengkap HobiBaca.`;
    });
    
    const pageKeywords = computed(() => {
      if (!category.value) return 'HobiBaca, kategori buku, baca buku online';
      
      const subcategories = category.value.subcategories || [];
      const keywords = [`kategori ${category.value.name}`, 'HobiBaca', 'buku online'];
      
      if (subcategories.length) {
        subcategories.forEach(sub => {
          keywords.push(sub.name);
        });
      }
      
      return keywords.join(', ');
    });
    
    // Set meta tags dynamically
    useHead({
      title: pageTitle,
      meta: [
        { name: 'description', content: pageDescription },
        { name: 'keywords', content: pageKeywords },
        // Open Graph
        { property: 'og:title', content: pageTitle },
        { property: 'og:description', content: pageDescription },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: computed(() => window.location.href) },
        // Twitter Card
        { name: 'twitter:card', content: 'summary' },
        { name: 'twitter:title', content: pageTitle },
        { name: 'twitter:description', content: pageDescription }
      ],
      link: [
        { 
          rel: 'canonical', 
          href: computed(() => window.location.href)
        }
      ]
    });
    
    return { category };
  },
  
  async created() {
    await this.fetchCategory();
  },
  
  watch: {
    '$route.params.id': 'fetchCategory'
  },
  
  methods: {
    async fetchCategory() {
      const categoryId = this.$route.params.id;
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/book-categories/${categoryId}/`);
        this.category = response.data;
      } catch (err) {
        console.error("Failed to fetch category", err);
        this.category = { name: "Kategori Tidak Ditemukan", subcategories: [] };
      }
    }
  }
};
</script>

<style scoped>
.category-container {
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
}

.loading {
  text-align: center;
  margin-top: 50px;
}

ul {
  padding-left: 20px;
  margin-bottom: 20px;
}

li {
  margin-bottom: 8px;
}

li a {
  color: #488e71;
  text-decoration: none;
  transition: color 0.3s;
}

li a:hover {
  color: #f66a24;
  text-decoration: underline;
}

h3 {
  color: #333;
  margin-bottom: 20px;
}

h4 {
  color: #444;
  margin-top: 25px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}
</style>